import React, { useCallback, useEffect, useState } from 'react';
import {
  Shield,
  Plus,
  Search,
  Loader2,
  ArrowLeft,
  Users,
  Check,
  X,
  UserPlus,
  UserMinus,
  Crown,
  LogOut,
  Trash2,
  Clock,
  Trophy,
  Pencil,
} from 'lucide-react';
import { useSubPath } from '../lib/useSubPath';
import { api, type UserSearchResult } from '../lib/api';
import { Avatar } from './Avatar';
import { EmojiPicker } from './EmojiPicker';
import { BACKGROUNDS } from '../../shared/avatar';

type MyStatus = 'none' | 'pending' | 'member' | 'admin';

export interface LeagueSummary {
  id: string;
  name: string;
  description: string;
  /** Logo: emoji sobre un fondo del catálogo. Sin emoji, el escudo. */
  emoji: string;
  color: string;
  ownerName: string;
  members: number;
  createdAt: string;
  myStatus: MyStatus;
}

interface LeaguePerson {
  id: string;
  username: string;
  avatar: unknown;
  dRating: number;
  role: 'admin' | 'member';
  isOwner: boolean;
  since: string;
}

interface LeagueDetail extends LeagueSummary {
  ownerId: string;
  membersList: LeaguePerson[];
  requests: LeaguePerson[];
}

interface Props {
  myId: string | null;
  onOpenUser: (id: string) => void;
  onLogin: () => void;
  /** Abre la clasificación filtrada por esta liga. */
  onLeaderboard: (leagueId: string) => void;
  notify: (msg: string) => void;
}

/**
 * Ligas: grupos con su propia clasificación. Se entra pidiéndolo (y un admin
 * acepta) o porque un admin te agrega. Los admins gestionan quién está.
 */
export const LeaguesView: React.FC<Props> = ({ myId, onOpenUser, onLogin, onLeaderboard, notify }) => {
  // La liga abierta va en la dirección (/ligas/<id>): recargar la mantiene.
  const [[openId], setSub] = useSubPath('/ligas');
  const setOpenId = (id: string | null) => setSub(id ? [id] : []);

  if (openId) {
    return (
      <LeagueDetailView
        id={openId}
        myId={myId}
        onBack={() => setOpenId(null)}
        onOpenUser={onOpenUser}
        onLeaderboard={onLeaderboard}
        notify={notify}
      />
    );
  }
  return <LeagueList myId={myId} onOpen={setOpenId} onLogin={onLogin} notify={notify} />;
};

/* ------------------------------------------------------------------ */

const LeagueList: React.FC<{
  myId: string | null;
  onOpen: (id: string) => void;
  onLogin: () => void;
  notify: (msg: string) => void;
}> = ({ myId, onOpen, onLogin, notify }) => {
  const [q, setQ] = useState('');
  const [leagues, setLeagues] = useState<LeagueSummary[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ leagues: LeagueSummary[] }>(`/leagues?q=${encodeURIComponent(q.trim())}`);
      setLeagues(res.leagues);
    } catch {
      setLeagues([]);
    }
  }, [q]);

  useEffect(() => {
    const h = window.setTimeout(() => void load(), q ? 250 : 0);
    return () => window.clearTimeout(h);
  }, [load, q]);

  const join = async (id: string) => {
    if (!myId) return onLogin();
    setBusy(id);
    try {
      await api.post(`/leagues/${id}/join`);
      notify('Solicitud enviada. Un admin de la liga tiene que aceptarla.');
      await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'No se pudo enviar la solicitud.');
    } finally {
      setBusy(null);
    }
  };

  const mine = (leagues ?? []).filter((l) => l.myStatus === 'admin' || l.myStatus === 'member');
  const others = (leagues ?? []).filter((l) => l.myStatus === 'none' || l.myStatus === 'pending');

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[12rem]">
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-sky-400" />
            Ligas
          </h1>
          <p className="text-ink-400 text-sm mt-1">
            Grupos con su propia clasificación: tu clase, tu equipo, tus amigos.
          </p>
        </div>
        <button
          onClick={() => (myId ? setCreating(true) : onLogin())}
          className="flex items-center gap-1.5 px-4 h-10 rounded-lg font-semibold text-sm bg-sky-600 hover:bg-sky-500 text-white transition-colors"
        >
          <Plus className="w-4 h-4" />
          Crear liga
        </button>
      </div>

      {creating && (
        <LeagueForm
          onCancel={() => setCreating(false)}
          onSaved={(id) => {
            setCreating(false);
            onOpen(id);
          }}
          notify={notify}
        />
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar ligas…"
          aria-label="Buscar ligas"
          type="search"
          className="w-full h-11 pl-9 pr-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm
                     placeholder:text-ink-500 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
        />
      </div>

      {leagues === null ? (
        <div className="h-24 shimmer rounded-lg" />
      ) : leagues.length === 0 ? (
        <div className="surface p-10 text-center text-ink-400 text-sm">
          {q ? 'Ninguna liga con ese nombre.' : 'Todavía no hay ligas. Crea la primera.'}
        </div>
      ) : (
        <>
          {mine.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-ink-300">Mis ligas</h2>
              <div className="surface divide-y divide-ink-800">
                {mine.map((l) => (
                  <LeagueRow key={l.id} league={l} onOpen={() => onOpen(l.id)} />
                ))}
              </div>
            </section>
          )}
          {others.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-ink-300">{mine.length ? 'Otras ligas' : 'Todas las ligas'}</h2>
              <div className="surface divide-y divide-ink-800">
                {others.map((l) => (
                  <LeagueRow key={l.id} league={l} onOpen={() => onOpen(l.id)}>
                    {l.myStatus === 'pending' ? (
                      <span className="flex items-center gap-1 text-xs text-ink-500">
                        <Clock className="w-3.5 h-3.5" /> Solicitud enviada
                      </span>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void join(l.id);
                        }}
                        disabled={busy === l.id}
                        className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:opacity-50 transition-colors"
                      >
                        {busy === l.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                        Pedir entrar
                      </button>
                    )}
                  </LeagueRow>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

const LeagueRow: React.FC<{ league: LeagueSummary; onOpen: () => void; children?: React.ReactNode }> = ({
  league,
  onOpen,
  children,
}) => (
  <div className="flex items-center gap-3 px-4 py-3">
    <button onClick={onOpen} className="flex items-center gap-3 min-w-0 flex-1 text-left">
      <LeagueBadge emoji={league.emoji} color={league.color} />
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className="font-semibold text-ink-50 truncate">{league.name}</span>
          {league.myStatus === 'admin' && (
            <span className="chip text-[10px] border-ember-600 bg-ember-500/10 text-ember-300 shrink-0">Admin</span>
          )}
        </span>
        <span className="block text-xs text-ink-500 truncate">
          {league.members} {league.members === 1 ? 'miembro' : 'miembros'} · creada por {league.ownerName}
        </span>
      </span>
    </button>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */

const LeagueForm: React.FC<{
  initial?: { id: string; name: string; description: string; emoji: string; color: string };
  onCancel: () => void;
  onSaved: (id: string) => void;
  notify: (msg: string) => void;
}> = ({ initial, onCancel, onSaved, notify }) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [emoji, setEmoji] = useState(initial?.emoji ?? '');
  const [color, setColor] = useState(initial?.color ?? 'sky');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      if (initial) {
        await api.patch(`/leagues/${initial.id}`, { name, description, emoji, color });
        onSaved(initial.id);
      } else {
        const res = await api.post<{ id: string }>('/leagues', { name, description, emoji, color });
        notify('Liga creada. Eres su admin.');
        onSaved(res.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface p-4 space-y-3">
      <h2 className="font-semibold text-ink-100">{initial ? 'Editar liga' : 'Nueva liga'}</h2>

      {/* Logo */}
      <div className="flex items-start gap-4 flex-wrap">
        <LeagueBadge emoji={emoji} color={color} size={72} />
        <div className="flex-1 min-w-[14rem] space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-ink-400">Logo</span>
            <EmojiPicker value={emoji || '🛡️'} onChange={setEmoji} />
            {emoji && (
              <button type="button" onClick={() => setEmoji('')} className="text-xs text-ink-500 hover:text-ink-200">
                Usar el escudo
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Color del logo">
            {BACKGROUNDS.map((b) => (
              <button
                key={b.id}
                type="button"
                role="radio"
                aria-checked={color === b.id}
                aria-label={b.label}
                title={b.label}
                onClick={() => setColor(b.id)}
                className={`w-7 h-7 rounded-full border-2 transition-transform ${
                  color === b.id ? 'border-sky-400 scale-110' : 'border-transparent hover:scale-110'
                }`}
                style={{ background: `linear-gradient(135deg, ${b.from}, ${b.to})` }}
              />
            ))}
          </div>
        </div>
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={40}
        placeholder="Nombre de la liga"
        aria-label="Nombre de la liga"
        autoFocus
        className="w-full h-11 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-sky-500"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={200}
        rows={2}
        placeholder="Descripción (opcional)"
        aria-label="Descripción de la liga"
        className="w-full px-3 py-2 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-sky-500 resize-none"
      />
      {error && <p className="text-danger-400 text-sm">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 h-10 rounded-lg text-sm font-medium text-ink-400 hover:text-ink-100">
          Cancelar
        </button>
        <button
          onClick={() => void save()}
          disabled={busy || name.trim().length < 3}
          className="flex items-center gap-1.5 px-4 h-10 rounded-lg text-sm font-semibold bg-sky-600 hover:bg-sky-500 text-white disabled:bg-ink-800 disabled:text-ink-500"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {initial ? 'Guardar' : 'Crear liga'}
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */

const LeagueDetailView: React.FC<{
  id: string;
  myId: string | null;
  onBack: () => void;
  onOpenUser: (id: string) => void;
  onLeaderboard: (leagueId: string) => void;
  notify: (msg: string) => void;
}> = ({ id, myId, onBack, onOpenUser, onLeaderboard, notify }) => {
  const [league, setLeague] = useState<LeagueDetail | null | undefined>(undefined);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ league: LeagueDetail }>(`/leagues/${id}`);
      setLeague(res.league);
    } catch {
      setLeague(null);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (key: string, run: () => Promise<unknown>, done?: string, after?: () => void) => {
    setBusy(key);
    try {
      await run();
      if (done) notify(done);
      if (after) after();
      else await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'No se pudo completar.');
    } finally {
      setBusy(null);
    }
  };

  const back = (
    <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-ink-400 hover:text-ink-100 transition-colors">
      <ArrowLeft className="w-4 h-4" />
      Ligas
    </button>
  );

  if (league === undefined) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {back}
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      </div>
    );
  }
  if (league === null) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {back}
        <div className="surface p-12 text-center text-ink-400">Esa liga ya no existe.</div>
      </div>
    );
  }

  const isAdmin = league.myStatus === 'admin';
  const isMember = isAdmin || league.myStatus === 'member';
  const isOwner = myId === league.ownerId;
  const memberIds = new Set(league.membersList.map((m) => m.id));

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      {back}

      {editing ? (
        <LeagueForm
          initial={{ id: league.id, name: league.name, description: league.description, emoji: league.emoji, color: league.color }}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
          notify={notify}
        />
      ) : (
        <div className="surface-raised p-5 flex items-start gap-4 flex-wrap">
          <LeagueBadge emoji={league.emoji} color={league.color} size={64} />
          <div className="flex-1 min-w-[12rem]">
            <h1 className="text-2xl font-display font-bold text-ink-50">{league.name}</h1>
            {league.description && <p className="text-ink-300 text-sm mt-1">{league.description}</p>}
            <p className="text-ink-500 text-xs mt-1">
              {league.members} {league.members === 1 ? 'miembro' : 'miembros'} · creada por {league.ownerName}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isMember && (
              <button
                onClick={() => onLeaderboard(league.id)}
                className="flex items-center gap-1.5 px-3 h-10 rounded-lg text-sm font-semibold bg-ember-500 hover:bg-ember-400 text-on-accent"
              >
                <Trophy className="w-4 h-4" /> Clasificación
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-1.5 px-3 h-10 rounded-lg text-sm font-semibold border border-ink-700 bg-ink-850 text-ink-200 hover:bg-ink-800"
              >
                <Pencil className="w-4 h-4" /> Editar
              </button>
            )}
            {!isMember && myId && (
              league.myStatus === 'pending' ? (
                <span className="flex items-center gap-1.5 px-3 h-10 text-sm text-ink-400">
                  <Clock className="w-4 h-4" /> Solicitud enviada
                </span>
              ) : (
                <button
                  onClick={() =>
                    void act('join', () => api.post(`/leagues/${league.id}/join`), 'Solicitud enviada. Un admin tiene que aceptarla.')
                  }
                  disabled={busy === 'join'}
                  className="flex items-center gap-1.5 px-3 h-10 rounded-lg text-sm font-semibold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:opacity-50"
                >
                  <UserPlus className="w-4 h-4" /> Pedir entrar
                </button>
              )
            )}
          </div>
        </div>
      )}

      {!isMember && (
        <p className="text-ink-500 text-sm text-center">
          Los miembros y la clasificación de la liga solo los ven quienes pertenecen a ella.
        </p>
      )}

      {/* Solicitudes */}
      {isAdmin && league.requests.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-ink-300 flex items-center gap-1.5">
            Solicitudes para entrar
            <span className="min-w-5 h-5 px-1.5 rounded-full text-[0.6875rem] font-bold bg-ember-500 text-on-accent flex items-center justify-center">
              {league.requests.length}
            </span>
          </h2>
          <div className="surface divide-y divide-ink-800">
            {league.requests.map((p) => (
              <PersonRow key={p.id} person={p} onOpen={() => onOpenUser(p.id)} sub="Quiere entrar">
                <button
                  onClick={() =>
                    void act(`a-${p.id}`, () => api.post(`/leagues/${league.id}/requests/${p.id}/accept`), `${p.username} ya es de la liga.`)
                  }
                  disabled={busy === `a-${p.id}`}
                  className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50"
                >
                  <Check className="w-4 h-4" /> Aceptar
                </button>
                <IconBtn
                  label={`Rechazar a ${p.username}`}
                  onClick={() => void act(`r-${p.id}`, () => api.delete(`/leagues/${league.id}/members/${p.id}`), 'Solicitud rechazada.')}
                >
                  <X className="w-4 h-4" />
                </IconBtn>
              </PersonRow>
            ))}
          </div>
        </section>
      )}

      {/* Miembros */}
      {isMember && (
        <section className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-300 flex items-center gap-1.5">
              <Users className="w-4 h-4" /> Miembros ({league.membersList.length})
            </h2>
            {isAdmin && (
              <button
                onClick={() => setAdding((v) => !v)}
                className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold bg-sky-600 hover:bg-sky-500 text-white"
              >
                <UserPlus className="w-4 h-4" /> {adding ? 'Cerrar' : 'Agregar miembros'}
              </button>
            )}
          </div>

          {isAdmin && adding && (
            <AddMembers
              exclude={memberIds}
              onAdd={(u) =>
                act(`add-${u.id}`, () => api.post(`/leagues/${league.id}/members`, { userId: u.id }), `${u.username} agregado a la liga.`)
              }
              busy={busy}
            />
          )}

          <div className="surface divide-y divide-ink-800">
            {league.membersList.map((p) => (
              <PersonRow
                key={p.id}
                person={p}
                onOpen={() => onOpenUser(p.id)}
                sub={p.isOwner ? 'Dueño de la liga' : p.role === 'admin' ? 'Admin' : `D-Rating ${p.dRating}`}
                badge={p.role === 'admin'}
              >
                {isAdmin && !p.isOwner && p.id !== myId && (
                  <>
                    <button
                      onClick={() =>
                        void act(
                          `role-${p.id}`,
                          () =>
                            api.put(`/leagues/${league.id}/members/${p.id}/role`, {
                              role: p.role === 'admin' ? 'member' : 'admin',
                            }),
                          p.role === 'admin' ? `${p.username} ya no es admin.` : `${p.username} ahora es admin.`,
                        )
                      }
                      disabled={busy === `role-${p.id}`}
                      className="inline-flex px-2 h-9 items-center rounded-lg text-xs font-medium whitespace-nowrap text-ink-400 hover:text-ink-100 hover:bg-ink-850"
                    >
                      {p.role === 'admin' ? 'Quitar admin' : 'Hacer admin'}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`¿Quitar a ${p.username} de ${league.name}?`)) {
                          void act(`rm-${p.id}`, () => api.delete(`/leagues/${league.id}/members/${p.id}`), `${p.username} ya no es de la liga.`);
                        }
                      }}
                      disabled={busy === `rm-${p.id}`}
                      aria-label={`Quitar a ${p.username} de la liga`}
                      className="flex items-center gap-1 px-2.5 h-9 rounded-lg text-xs font-semibold text-danger-400 border border-danger-500/40 hover:bg-danger-500/10 disabled:opacity-50 whitespace-nowrap"
                    >
                      <UserMinus className="w-3.5 h-3.5" />
                      Quitar
                    </button>
                  </>
                )}
              </PersonRow>
            ))}
          </div>
        </section>
      )}

      {/* Salir o borrar */}
      {isMember && (
        <div className="flex justify-center">
          {isOwner ? (
            <button
              onClick={() => {
                if (window.confirm(`¿Borrar la liga ${league.name}? Se pierde para todos sus miembros.`)) {
                  void act('del', () => api.delete(`/leagues/${league.id}`), 'Liga borrada.', onBack);
                }
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-danger-400 hover:bg-danger-500/10"
            >
              <Trash2 className="w-4 h-4" /> Borrar la liga
            </button>
          ) : (
            <button
              onClick={() => {
                if (window.confirm(`¿Salir de ${league.name}?`)) {
                  void act('leave', () => api.delete(`/leagues/${league.id}/members/${myId}`), 'Saliste de la liga.', onBack);
                }
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-ink-400 hover:text-ink-100 hover:bg-ink-850"
            >
              <LogOut className="w-4 h-4" /> Salir de la liga
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/** Buscador de cuentas para agregar a la liga a mano. */
const AddMembers: React.FC<{
  exclude: Set<string>;
  onAdd: (u: UserSearchResult) => void;
  busy: string | null;
}> = ({ exclude, onAdd, busy }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<UserSearchResult[] | null>(null);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults(null);
      return;
    }
    const h = window.setTimeout(async () => {
      try {
        const res = await api.get<{ users: UserSearchResult[] }>(`/users/search?q=${encodeURIComponent(term)}`);
        setResults(res.users);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => window.clearTimeout(h);
  }, [q]);

  return (
    <div className="surface p-3 space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Busca por nombre de usuario…"
          aria-label="Buscar cuentas para agregar"
          type="search"
          autoFocus
          className="w-full h-10 pl-9 pr-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-sky-500"
        />
      </div>
      {results !== null &&
        (results.length === 0 ? (
          <p className="text-ink-500 text-sm px-1">Nadie con ese usuario.</p>
        ) : (
          <div className="divide-y divide-ink-800 max-h-64 overflow-y-auto">
            {results.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-1 py-2">
                <Avatar config={u.avatar as never} size={32} />
                <span className="flex-1 min-w-0 truncate text-sm text-ink-100">{u.username}</span>
                {exclude.has(u.id) ? (
                  <span className="text-xs text-ink-500 flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" /> Ya está
                  </span>
                ) : (
                  <button
                    onClick={() => onAdd(u)}
                    disabled={busy === `add-${u.id}`}
                    className="flex items-center gap-1 px-3 h-8 rounded-lg text-xs font-semibold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:opacity-50"
                  >
                    {busy === `add-${u.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Agregar
                  </button>
                )}
              </div>
            ))}
          </div>
        ))}
    </div>
  );
};

const PersonRow: React.FC<{
  person: LeaguePerson;
  onOpen: () => void;
  sub: string;
  badge?: boolean;
  children?: React.ReactNode;
}> = ({ person, onOpen, sub, badge, children }) => (
  <div className="flex items-center gap-3 px-4 py-2.5">
    <button onClick={onOpen} className="shrink-0" aria-label={`Ver el perfil de ${person.username}`}>
      <Avatar config={person.avatar as never} size={36} />
    </button>
    <button onClick={onOpen} className="min-w-0 flex-1 text-left">
      <span className="flex items-center gap-1.5">
        <span className="font-medium text-ink-100 truncate">{person.username}</span>
        {badge && <Crown className="w-3.5 h-3.5 text-ember-400 shrink-0" />}
      </span>
      <span className="block text-xs text-ink-500 truncate">{sub}</span>
    </button>
    <div className="flex items-center gap-1 shrink-0">{children}</div>
  </div>
);

const IconBtn: React.FC<{ label: string; danger?: boolean; onClick: () => void; children: React.ReactNode }> = ({
  label,
  danger,
  onClick,
  children,
}) => (
  <button
    onClick={onClick}
    aria-label={label}
    title={label}
    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
      danger ? 'text-ink-500 hover:text-danger-400 hover:bg-danger-500/10' : 'text-ink-400 hover:text-ink-100 hover:bg-ink-850'
    }`}
  >
    {children}
  </button>
);

/** El logo de una liga: su emoji (o el escudo) sobre su color. */
export const LeagueBadge: React.FC<{ emoji: string; color: string; size?: number }> = ({ emoji, color, size = 40 }) => {
  const bg = BACKGROUNDS.find((b) => b.id === color) ?? BACKGROUNDS[0];
  return (
    <span
      className="rounded-[30%] flex items-center justify-center shrink-0 shadow-sm ring-1 ring-black/10"
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${bg.from}, ${bg.to})` }}
      aria-hidden="true"
    >
      {emoji ? (
        <span className="font-emoji leading-none" style={{ fontSize: size * 0.55 }}>
          {emoji}
        </span>
      ) : (
        <Shield className="text-white drop-shadow" style={{ width: size * 0.5, height: size * 0.5 }} />
      )}
    </span>
  );
};
